import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiArrowRight } from 'react-icons/fi';
import { motion } from 'framer-motion';
import Cookies from 'js-cookie';
import { useDispatch } from 'react-redux';
import { getLoginAction } from '../redux/actions';
import { loginUser } from '../api/authAPI';

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await loginUser(formData);
      const { user, accessToken, refreshToken } = data;
      Cookies.set('access-token', accessToken);
      Cookies.set('refresh-token', refreshToken);
      dispatch(getLoginAction(user));
      navigate('/');
    } catch (err) {
      setError(err || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const formVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  const inputVariants = {
    focus: { scale: 1.02, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)' },
    blur: { scale: 1, boxShadow: '0px 0px 0px rgba(0, 0, 0, 0)' }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-green-200 to-blue-300 p-4">
      <motion.div
        className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <h2 className="text-3xl font-bold text-center text-green-600 mb-2">
            Welcome Back
          </h2>
          <p className="text-gray-500 text-center mb-6">
            Login to access your KrishiMart account
          </p>
        </motion.div>

        {error &&
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 text-red-600 p-3 rounded-md mb-4 text-sm"
          >
            {error}
          </motion.div>}

        <motion.form
          className="space-y-5"
          onSubmit={handleSubmit}
          variants={formVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="relative">
            <FiMail className="absolute left-3 top-3.5 text-gray-400" />
            <motion.input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              required
              whileFocus="focus"
              variants={inputVariants}
              className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all"
            />
          </div>

          <div className="relative">
            <FiLock className="absolute left-3 top-3.5 text-gray-400" />
            <motion.input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              required
              whileFocus="focus"
              variants={inputVariants}
              className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>

          <div className="flex justify-between items-center text-sm">
            <label className="flex items-center">
              <input type="checkbox" className="mr-2 h-4 w-4 text-green-500" />
              <span className="text-gray-600">Remember me</span>
            </label>
            <a href="#" className="text-blue-500 hover:text-blue-600">
              Forgot password?
            </a>
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="w-full bg-green-500 text-white p-3 rounded-lg hover:bg-green-600 transition flex items-center justify-center space-x-2 font-medium"
            disabled={loading}
          >
            <span>
              {loading ? 'Logging in...' : 'Login'}
            </span>
            {!loading && <FiArrowRight />}
          </motion.button>
        </motion.form>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-6 text-center"
        >
          <p className="text-gray-600">
            Don't have an account?{' '}
            <motion.button
              onClick={() => navigate('/register')}
              className="text-blue-500 hover:text-blue-600 font-medium"
              whileHover={{ scale: 1.05 }}
            >
              Register Now
            </motion.button>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;
